// Cloud sign-in and key unlock (SECURITY.md §4, §5.1).
// 1. Supabase Auth sign-in, connect PowerSync, wait for the first download.
// 2. User keypair: reuse the server copy (unwrapped with the password) or create
//    one. A new keypair is only trusted after the server has accepted it; if
//    another device won that race, adopt the server copy instead.
// 3. Clinic key: unwrap from a grant. If none exists and no clinic key exists,
//    the owner creates one, again confirmed by the server before use.
// First sign-in on a device therefore needs internet; later sign-ins do not,
// because the unlocked key lives in the device keystore.
import type { PowerSyncDatabase } from '@powersync/web';
import { getSupabase } from '@/lib/supabase';
import { connectIfSignedIn, newId, nowIso, waitForUploadsDrained } from '@/lib/db/db';
import { deriveWrappingKey, newKdfParams, type KdfParams } from '@/lib/crypto/kdf';
import { generateUserKeypair, unwrapClinicKey, unwrapPrivateKey, wrapPrivateKey, wrapClinicKeyFor, KEY_ALGO, type WrappedBlob } from '@/lib/crypto/keys';
import { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { generateAesKey } from '@/lib/crypto/envelope';
import { clearClinicKeys } from '@/lib/crypto/keystore';
import type { Role, Session } from '@/app/state';
import { ensureNumberBlocks } from './numbers';

const DEVICE_KEY = 'klx.deviceId';

interface UserKeyRow { id: string; public_key: string; private_key_wrapped: string; kdf: string }
interface GrantRow { key_version: number; wrapped_key: string; grantee_pubkey: string }

async function waitForFirstSync(db: PowerSyncDatabase, timeoutMs = 30000) {
  await Promise.race([db.waitForFirstSync(), new Promise((_, rej) => setTimeout(() => rej(new Error('Sync timed out. Check the connection and try again.')), timeoutMs))]);
}

/** Push local changes and pull the server's view of them. */
async function roundTrip(db: PowerSyncDatabase) {
  await waitForUploadsDrained(db);
  // Give the replication stream a moment to deliver the server's copy back.
  await new Promise((r) => setTimeout(r, 1500));
}

async function unlockUserKeys(db: PowerSyncDatabase, userId: string, clinicId: string, password: string): Promise<{ privateKey: CryptoKey; publicKey: string }> {
  const serverRow = await db.getOptional<UserKeyRow>('select id, public_key, private_key_wrapped, kdf from user_keys where user_id = ?', [userId]);
  if (serverRow) {
    const wk = await deriveWrappingKey(password, JSON.parse(serverRow.kdf) as KdfParams);
    return { privateKey: await unwrapPrivateKey(wk, JSON.parse(serverRow.private_key_wrapped) as WrappedBlob), publicKey: serverRow.public_key };
  }
  // First device for this user: create and publish a keypair.
  const kp = await generateUserKeypair();
  const params = newKdfParams();
  const wk = await deriveWrappingKey(password, params);
  const blob = await wrapPrivateKey(wk, kp.privateJwk);
  const ts = nowIso();
  await db.execute('insert into user_keys (id, user_id, public_key, private_key_wrapped, kdf, created_at, updated_at) values (?, ?, ?, ?, ?, ?, ?)', [userId, userId, kp.publicKey, JSON.stringify(blob), JSON.stringify(params), ts, ts]);
  await db.execute('update clinic_members set public_key = ?, key_algo = ?, key_added_at = ? where clinic_id = ? and user_id = ?', [kp.publicKey, KEY_ALGO, ts, clinicId, userId]);
  await roundTrip(db);
  // Did our keypair win? Another device signing in at the same time may have published first.
  const after = await db.getOptional<UserKeyRow>('select id, public_key, private_key_wrapped, kdf from user_keys where user_id = ?', [userId]);
  if (after && after.public_key !== kp.publicKey) {
    const wk2 = await deriveWrappingKey(password, JSON.parse(after.kdf) as KdfParams);
    const pk = await unwrapPrivateKey(wk2, JSON.parse(after.private_key_wrapped) as WrappedBlob);
    await db.execute('update clinic_members set public_key = ?, key_algo = ?, key_added_at = ? where clinic_id = ? and user_id = ?', [after.public_key, KEY_ALGO, ts, clinicId, userId]);
    return { privateKey: pk, publicKey: after.public_key };
  }
  return { privateKey: kp.privateKey, publicKey: kp.publicKey };
}

async function unlockClinicKey(db: PowerSyncDatabase, userId: string, clinicId: string, roles: Role[], me: { privateKey: CryptoKey; publicKey: string }): Promise<ClinicCrypto> {
  const findGrant = () => db.getOptional<GrantRow>(
    'select key_version, wrapped_key, grantee_pubkey from clinic_key_grants where clinic_id = ? and grantee_user_id = ? and grantee_pubkey = ? and revoked_at is null order by key_version desc limit 1',
    [clinicId, userId, me.publicKey]);

  const grant = await findGrant();
  if (grant) {
    const raw = await unwrapClinicKey(me.privateKey, JSON.parse(grant.wrapped_key) as WrappedBlob);
    return ClinicCrypto.fromRaw(clinicId, grant.key_version, raw);
  }

  const existing = await db.getOptional<{ id: string }>('select id from clinic_keys where clinic_id = ? and status = ?', [clinicId, 'active']);
  if (existing || !roles.includes('owner')) {
    throw new Error('Waiting for a colleague to share the clinic key with you. Ask them to open Klinixis while online, then sign in again.');
  }

  // Brand-new clinic and we are the owner: create the first key, confirm the server kept it.
  const raw = await generateAesKey();
  const ts = nowIso();
  const keyId = newId();
  await db.execute('insert into clinic_keys (id, clinic_id, key_version, algo, status, created_by, created_at) values (?, ?, 1, ?, ?, ?, ?)', [keyId, clinicId, 'AES-256-GCM', 'active', userId, ts]);
  await db.execute('insert into clinic_key_grants (id, clinic_id, key_version, grantee_user_id, grantee_pubkey, wrapped_key, granted_by, created_at) values (?, ?, 1, ?, ?, ?, ?, ?)',
    [newId(), clinicId, userId, me.publicKey, JSON.stringify(await wrapClinicKeyFor(me.publicKey, raw)), userId, ts]);
  await roundTrip(db);
  const winner = await db.getOptional<{ id: string }>('select id from clinic_keys where clinic_id = ? and key_version = 1', [clinicId]);
  if (winner && winner.id !== keyId) {
    // Lost the race: another device created the clinic key first. Use its grant if it wrapped for our key.
    await clearClinicKeys(clinicId);
    const g = await findGrant();
    if (!g) throw new Error('Another device set up this clinic first. Open Klinixis on that device while online so it can share the key, then sign in again.');
    const r = await unwrapClinicKey(me.privateKey, JSON.parse(g.wrapped_key) as WrappedBlob);
    return ClinicCrypto.fromRaw(clinicId, g.key_version, r);
  }
  return ClinicCrypto.fromRaw(clinicId, 1, raw);
}

export async function signInAndUnlock(db: PowerSyncDatabase, email: string, password: string): Promise<{ session: Session; crypto: ClinicCrypto }> {
  const sb = getSupabase();
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error || !data.user) throw new Error(error?.message ?? 'sign-in failed');
  const userId = data.user.id;
  await connectIfSignedIn(db);
  await waitForFirstSync(db);

  const roleRows = await db.getAll<{ clinic_id: string; role: Role }>('select clinic_id, role from clinic_member_roles where user_id = ?', [userId]);
  const clinicId = roleRows[0]?.clinic_id;
  if (!clinicId) throw new Error('This account is not a member of any clinic yet.');
  const roles = roleRows.filter((r) => r.clinic_id === clinicId).map((r) => r.role);

  const me = await unlockUserKeys(db, userId, clinicId, password);
  const crypto = await unlockClinicKey(db, userId, clinicId, roles, me);

  // Device enrolment (one row per browser profile)
  let deviceId: string | null = null;
  try { deviceId = localStorage.getItem(DEVICE_KEY); } catch { /* ignore */ }
  const known = deviceId ? await db.getOptional<{ id: string }>('select id from devices where id = ?', [deviceId]) : null;
  if (!deviceId || !known) {
    deviceId = newId();
    const ts = nowIso();
    await db.execute('insert into devices (id, clinic_id, name, platform, token_prefix, is_token_issuer, enrolled_by, enrolled_at, created_at, updated_at) values (?, ?, ?, ?, ?, 0, ?, ?, ?, ?)',
      [deviceId, clinicId, navigator.platform || 'browser', /Android/i.test(navigator.userAgent) ? 'android' : /Win/i.test(navigator.userAgent) ? 'windows' : /Mac/i.test(navigator.userAgent) ? 'macos' : 'other', '', userId, ts, ts, ts]);
    try { localStorage.setItem(DEVICE_KEY, deviceId); } catch { /* ignore */ }
  }
  const dev = await db.getOptional<{ token_prefix: string }>('select token_prefix from devices where id = ?', [deviceId]);

  void grantPendingMembers(db, clinicId, userId, crypto).catch((e) => console.warn('peer grant skipped', e));
  // The device row must be on the server before it can reserve numbers.
  void waitForUploadsDrained(db).then(() => ensureNumberBlocks(db, clinicId, deviceId)).catch((e) => console.warn('number block skipped', e));
  return { session: { mode: 'cloud', userId, clinicId, deviceId, tokenPrefix: dev?.token_prefix ?? '', roles }, crypto };
}

/** Peer recovery: wrap the clinic key for members who published a key but have no grant for it. */
export async function grantPendingMembers(db: PowerSyncDatabase, clinicId: string, byUserId: string, crypto: ClinicCrypto) {
  const pending = await db.getAll<{ user_id: string; public_key: string }>(
    `select m.user_id, m.public_key from clinic_members m where m.clinic_id = ? and m.status = 'active' and m.public_key is not null
       and not exists (select 1 from clinic_key_grants g where g.clinic_id = m.clinic_id and g.grantee_user_id = m.user_id and g.grantee_pubkey = m.public_key and g.revoked_at is null)`, [clinicId]);
  if (pending.length === 0 || !crypto.canGrant) return;
  const raw = crypto.exportRawForWrap();
  for (const m of pending) {
    const blob = await wrapClinicKeyFor(m.public_key, raw);
    await db.execute('insert into clinic_key_grants (id, clinic_id, key_version, grantee_user_id, grantee_pubkey, wrapped_key, granted_by, created_at) values (?, ?, ?, ?, ?, ?, ?, ?)',
      [newId(), clinicId, crypto.active.version, m.user_id, m.public_key, JSON.stringify(blob), byUserId, nowIso()]);
  }
}
